import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Briefcase, Calculator, TrendingUp, ImagePlus, Search, Download, Phone, MessageCircle, Mail, ChevronDown, ChevronRight, PlusCircle, X } from "lucide-react";

const TABS = ["overview", "leads", "estimates", "projects", "portfolio"];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Lead status vocabulary (new admin spec)
const STATUSES = [
  { v: "new",           label: "New",                    color: "bg-blue-100 text-blue-800" },
  { v: "contacted",     label: "Contacted",              color: "bg-amber-100 text-amber-800" },
  { v: "qualified",     label: "Qualified",              color: "bg-purple-100 text-purple-800" },
  { v: "consulted",     label: "Consultation Scheduled", color: "bg-teal-100 text-teal-800" },
  { v: "won",           label: "Won",                    color: "bg-green-100 text-green-800" },
  { v: "lost",          label: "Lost",                   color: "bg-red-100 text-red-800" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.v, s]));

const SOURCES = [
  "Website Consultation",
  "Website Consultation Popup",
  "Website Estimator",
  "Website Contact",
  "AI Assistant",
  "Referral",
  "Instagram",
  "Other",
];

// Normalize Indian phone → digits-only + 91 prefix for wa.me / tel:
const digits = (s) => (s || "").replace(/\D/g, "");
const toWa = (phone) => {
  const d = digits(phone);
  if (!d) return null;
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith("91")) return d;
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
  return d;
};
const toTel = (phone) => {
  const d = digits(phone);
  if (!d) return null;
  if (d.length === 10) return `+91${d}`;
  if (d.length >= 12) return `+${d}`;
  return `+${d}`;
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const isOverdue = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now() - 24 * 3600 * 1000; } catch { return false; }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [newProj, setNewProj] = useState({ title: "", location: "", bhk: "3BHK", finish_level: "premium", status: "design", quotation_amount: "" });
  const [newPort, setNewPort] = useState({ title: "", location: "", bhk: "3BHK", style: "Modern", description: "", images: [], cover_image_id: null });
  const [portUploading, setPortUploading] = useState(false);

  // Project edit
  const [editingProj, setEditingProj] = useState(null); // project id
  const [projDraft, setProjDraft] = useState({});

  // Portfolio edit
  const [editingPort, setEditingPort] = useState(null);
  const [portEditDraft, setPortEditDraft] = useState({});
  const [portEditUploading, setPortEditUploading] = useState(false);

  // Leads UI state
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [fAssigned, setFAssigned] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [expanded, setExpanded] = useState(null); // lead id
  const [drafts, setDrafts] = useState({});       // per-lead unsaved fields
  const [historyByLead, setHistoryByLead] = useState({}); // lead_id → estimates[]
  const [backfillLoading, setBackfillLoading] = useState(false);

  // Add-lead modal
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadSaving, setAddLeadSaving] = useState(false);
  const emptyLead = { name: "", phone: "", email: "", source: "Walk-in", apartment_type: "", apartment_size: "", budget: "", message: "" };
  const [newLead, setNewLead] = useState(emptyLead);

  // Delete-lead confirm (inside drawer)
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Change password
  const [cpOpen, setCpOpen] = useState(false);
  const [cpSaving, setCpSaving] = useState(false);
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });

  const reload = async () => {
    const [s, l, e, p, po] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/estimates"),
      api.get("/admin/projects"), api.get("/admin/portfolio"),
    ]);
    setStats(s.data); setLeads(l.data); setEstimates(e.data); setProjects(p.data); setPortfolio(po.data);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const assignedOptions = useMemo(() => {
    const s = new Set(leads.map((l) => l.assigned_to).filter(Boolean));
    return Array.from(s).sort();
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = leads.filter((l) => {
      if (term) {
        const hay = `${l.name || ""} ${l.email || ""} ${l.phone || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (fStatus !== "all" && (l.status || "new") !== fStatus) return false;
      if (fSource !== "all") {
        const src = l.source || "Unknown";
        if (fSource === "Unknown" ? src && src !== "Unknown" : src !== fSource) return false;
      }
      if (fAssigned !== "all") {
        if (fAssigned === "__unassigned__" ? !!l.assigned_to : l.assigned_to !== fAssigned) return false;
      }
      return true;
    });
    const byTime = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === "newest") out.sort(byTime);
    if (sortBy === "oldest") out.sort((a, b) => -byTime(a, b));
    if (sortBy === "follow") out.sort((a, b) => new Date(a.follow_up_date || "9999") - new Date(b.follow_up_date || "9999"));
    if (sortBy === "name") out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return out;
  }, [leads, q, fStatus, fSource, fAssigned, sortBy]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const patchLead = async (id, patch) => {
    await api.patch(`/admin/leads/${id}`, patch);
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const saveDraft = async (id) => {
    const d = drafts[id] || {};
    if (Object.keys(d).length === 0) { toast("No changes"); return; }
    try {
      await patchLead(id, d);
      toast.success("Lead updated");
      setDrafts((p) => ({ ...p, [id]: {} }));
    } catch { toast.error("Failed to save"); }
  };

  const setDraft = (id, field, value) => {
    setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  };

  const val = (l, f) => (drafts[l.id]?.[f] !== undefined ? drafts[l.id][f] : (l[f] ?? ""));

  const deleteLead = async (id) => {
    if (!id) return;
    setDeleteSaving(true);
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      setExpanded(null);
      setDeleteConfirm(false);
      await reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to delete lead";
      toast.error(msg);
    } finally {
      setDeleteSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dreamy-leads-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch { toast.error("Export failed"); }
  };

  const runBackfill = async () => {
    if (!window.confirm("Run one-time backfill? This links every existing estimate to a lead (safe & idempotent).")) return;
    setBackfillLoading(true);
    try {
      const { data } = await api.post("/admin/backfill/estimates");
      toast.success(`Linked ${data.linked} · New leads ${data.new_leads_created} · Ambiguous ${data.ambiguous.length}`);
      await reload();
    } catch { toast.error("Backfill failed"); } finally { setBackfillLoading(false); }
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    if (cp.next.length < 10) { toast.error("New password must be at least 10 characters."); return; }
    if (cp.next !== cp.confirm) { toast.error("New passwords do not match."); return; }
    if (cp.next === cp.current) { toast.error("New password must differ from the current password."); return; }
    setCpSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cp.current, new_password: cp.next });
      toast.success("Password updated. Please sign in again.");
      setCpOpen(false);
      setCp({ current: "", next: "", confirm: "" });
      logout();
      window.location.replace("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setCpSaving(false); }
  };

  const submitNewLead = async (e) => {
    e.preventDefault();
    if (!newLead.name.trim() || !newLead.phone.trim() || !newLead.email.trim()) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setAddLeadSaving(true);
    try {
      await api.post("/leads", {
        name: newLead.name.trim(),
        email: newLead.email.trim(),
        phone: newLead.phone.trim(),
        source: newLead.source || "Walk-in",
        apartment_type: newLead.apartment_type || null,
        apartment_size: newLead.apartment_size ? Number(newLead.apartment_size) : null,
        budget: newLead.budget || null,
        message: newLead.message || null,
      });
      toast.success("Lead added");
      setNewLead(emptyLead);
      setAddLeadOpen(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add lead");
    } finally { setAddLeadSaving(false); }
  };

  const toggleExpand = async (leadId) => {
    const isOpen = expanded === leadId;
    setExpanded(isOpen ? null : leadId);
    if (!isOpen && !historyByLead[leadId]) {
      try {
        const { data } = await api.get(`/admin/leads/${leadId}/estimates`);
        setHistoryByLead((p) => ({ ...p, [leadId]: data }));
      } catch { /* silent */ }
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    await api.post("/admin/projects", { ...newProj, quotation_amount: newProj.quotation_amount ? Number(newProj.quotation_amount) : null });
    toast.success("Project created");
    setNewProj({ title: "", location: "", bhk: "3BHK", finish_level: "premium", status: "design", quotation_amount: "" });
    reload();
  };

  const startEditProject = (p) => {
    setEditingProj(p.id);
    setProjDraft({
      title: p.title || "", location: p.location || "", bhk: p.bhk || "3BHK",
      finish_level: p.finish_level || "premium", status: p.status || "design",
      quotation_amount: p.quotation_amount ?? "",
    });
  };

  const saveEditProject = async (id) => {
    try {
      await api.patch(`/admin/projects/${id}`, {
        ...projDraft,
        quotation_amount: projDraft.quotation_amount === "" || projDraft.quotation_amount == null
          ? null : Number(projDraft.quotation_amount),
      });
      toast.success("Project updated");
      setEditingProj(null); setProjDraft({});
      reload();
    } catch { toast.error("Failed to update project"); }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete this project?\n\n"${p.title}"\n\nThis action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      reload();
    } catch { toast.error("Failed to delete project"); }
  };

  // -------- Portfolio helpers --------
  const uploadOnePortfolioImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "portfolio");
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    // Use absolute API URL so <img src> resolves correctly
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return { id: crypto.randomUUID(), url: `${backend}${data.url}`, order: 0 };
  };

  const validateImageFile = (f) => {
    const ok = ["image/jpeg","image/jpg","image/png","image/webp"];
    if (!ok.includes(f.type)) { toast.error(`Unsupported type: ${f.name}`); return false; }
    if (f.size > 10 * 1024 * 1024) { toast.error(`Too large (max 10MB): ${f.name}`); return false; }
    return true;
  };

  const handlePortUpload = async (files, current, setCurrent, uploadingSetter) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const remaining = 10 - (current.images?.length || 0);
    if (remaining <= 0) { toast.error("Maximum 10 images per portfolio project."); return; }
    const toUpload = list.slice(0, remaining).filter(validateImageFile);
    if (list.length > remaining) toast(`Only added ${remaining} — 10-image limit reached.`);
    uploadingSetter(true);
    try {
      const uploaded = [];
      for (const f of toUpload) { uploaded.push(await uploadOnePortfolioImage(f)); }
      const merged = [...(current.images || []), ...uploaded].map((im, i) => ({ ...im, order: i }));
      setCurrent({ ...current, images: merged, cover_image_id: current.cover_image_id || merged[0]?.id || null });
    } catch (err) { toast.error("Upload failed"); }
    finally { uploadingSetter(false); }
  };

  const removePortImage = (imgId, current, setCurrent) => {
    const nextImgs = (current.images || []).filter((im) => im.id !== imgId).map((im, i) => ({ ...im, order: i }));
    const nextCover = current.cover_image_id === imgId ? (nextImgs[0]?.id || null) : current.cover_image_id;
    setCurrent({ ...current, images: nextImgs, cover_image_id: nextCover });
  };

  const setPortCover = (imgId, current, setCurrent) => {
    setCurrent({ ...current, cover_image_id: imgId });
  };

  const addPortfolio = async (e) => {
    e.preventDefault();
    if (!newPort.title || !newPort.location) { toast.error("Title and location are required."); return; }
    if (!newPort.images || newPort.images.length === 0) { toast.error("Upload at least 1 image."); return; }
    try {
      await api.post("/admin/portfolio", { ...newPort });
      toast.success("Added to portfolio");
      setNewPort({ title: "", location: "", bhk: "3BHK", style: "Modern", description: "", images: [], cover_image_id: null });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const startEditPortfolio = (p) => {
    setEditingPort(p.id);
    setPortEditDraft({
      title: p.title || "", location: p.location || "", bhk: p.bhk || "3BHK",
      style: p.style || "Modern", description: p.description || "",
      images: p.images && p.images.length ? p.images : (p.cover_image ? [{ id: crypto.randomUUID(), url: p.cover_image, order: 0 }] : []),
      cover_image_id: p.cover_image_id || null,
    });
  };

  const saveEditPortfolio = async (id) => {
    if (!portEditDraft.images || portEditDraft.images.length === 0) { toast.error("At least 1 image is required."); return; }
    try {
      await api.patch(`/admin/portfolio/${id}`, {
        title: portEditDraft.title, location: portEditDraft.location, bhk: portEditDraft.bhk,
        style: portEditDraft.style, description: portEditDraft.description,
        images: portEditDraft.images, cover_image_id: portEditDraft.cover_image_id,
      });
      toast.success("Portfolio updated");
      setEditingPort(null); setPortEditDraft({});
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deletePortfolio = async (id) => {
    if (!window.confirm("Delete this portfolio item?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    toast.success("Removed");
    reload();
  };

  return (
    <div data-testid="admin-dashboard" className="grid grid-cols-1 lg:grid-cols-12 min-h-[85vh]">
      <aside className="lg:col-span-2 bg-[#1A1A1A] text-white p-6">
        <p className="overline text-[#D4AF37]">Admin</p>
        <p className="font-heading text-2xl mt-1">CRM</p>
        <nav className="mt-10 space-y-1">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`block w-full text-left px-3 py-2 text-sm uppercase tracking-[0.15em] transition-colors ${tab === t ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </nav>

        <div className="mt-10 pt-6 border-t border-white/10">
          <button data-testid="admin-change-password-btn" onClick={() => setCpOpen(true)} className="w-full text-left px-3 py-2 text-xs uppercase tracking-[0.15em] text-white/50 hover:text-white">
            Change password
          </button>
        </div>
      </aside>

      <section className="lg:col-span-10 p-8 md:p-10">
        {tab === "overview" && (
          <div>
            <p className="overline">Dashboard</p>
            <h1 className="font-heading text-4xl mt-2">Dashboard overview</h1>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Users, label: "Total Leads", v: stats.leads, sub: `${stats.new_leads || 0} new` },
                { icon: Briefcase, label: "Projects", v: stats.projects, sub: `${stats.active || 0} active` },
                { icon: Calculator, label: "Estimates Generated", v: stats.estimates, sub: "All time" },
                { icon: TrendingUp, label: "Pipeline Value", v: inr(stats.pipeline || 0), sub: "Quoted" },
              ].map(({ icon: Icon, label, v, sub }) => (
                <div key={label} className="border border-[#E8E6DF] p-6 bg-white">
                  <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] mt-4">{label}</p>
                  <p className="font-heading text-4xl mt-2">{v ?? 0}</p>
                  <p className="text-xs text-[#5C5C5C] mt-1">{sub}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="border border-[#E8E6DF] p-6">
                <p className="overline">Recent Leads</p>
                <ul className="mt-4 divide-y divide-[#E8E6DF]">
                  {leads.slice(0,5).map((l) => (
                    <li key={l.id} className="py-3 flex justify-between text-sm">
                      <span>{l.name} <span className="text-[#5C5C5C]">· {l.phone}</span></span>
                      <span className="text-xs uppercase text-[#B38F24]">{l.status}</span>
                    </li>
                  ))}
                  {leads.length === 0 && <li className="py-3 text-sm text-[#5C5C5C]">No leads yet.</li>}
                </ul>
              </div>
              <div className="border border-[#E8E6DF] p-6">
                <p className="overline">Recent Estimates</p>
                <ul className="mt-4 divide-y divide-[#E8E6DF]">
                  {estimates.slice(0,5).map((es) => (
                    <li key={es.id} className="py-3 flex justify-between text-sm">
                      <span>{es.bhk} · {es.size_sqft} sqft · <span className="capitalize">{es.finish_level}</span></span>
                      <span className="font-medium">{inr(es.total)}</span>
                    </li>
                  ))}
                  {estimates.length === 0 && <li className="py-3 text-sm text-[#5C5C5C]">No estimates yet.</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline">Leads</p>
                <h1 className="font-heading text-4xl mt-2">Lead inbox</h1>
                <p className="text-sm text-[#5C5C5C] mt-1">{filteredLeads.length} of {leads.length} leads</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="lead-add-btn" onClick={() => setAddLeadOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[#C9A24A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#B38F24]">
                  <PlusCircle size={14} /> Add Lead
                </button>
                <button data-testid="lead-backfill" onClick={runBackfill} disabled={backfillLoading} className="inline-flex items-center gap-2 rounded-full border border-[#E8E6DF] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:border-[#1C1A17] disabled:opacity-50">
                  {backfillLoading ? "Linking…" : "Backfill Estimates"}
                </button>
                <button data-testid="lead-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-[#1C1A17] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#2D2925]">
                  <Download size={14} /> Export CSV
                </button>
              </div>
            </div>

            {/* Search + filters bar */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-center bg-white border border-[#E8E6DF] p-3 rounded-lg">
              <div className="md:col-span-4 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847D]" />
                <input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone or email" className="w-full pl-9 pr-3 py-2 text-sm bg-transparent border border-[#E8E6DF] rounded-md focus:border-[#1C1A17] focus:outline-none" />
              </div>
              <select data-testid="lead-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="md:col-span-2 py-2 px-3 text-sm bg-transparent border border-[#E8E6DF] rounded-md focus:outline-none">
                <option value="all">All statuses</option>
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <select data-testid="lead-filter-source" value={fSource} onChange={(e) => setFSource(e.target.value)} className="md:col-span-2 py-2 px-3 text-sm bg-transparent border border-[#E8E6DF] rounded-md focus:outline-none">
                <option value="all">All sources</option>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="Unknown">Unknown / Existing</option>
              </select>
              <select data-testid="lead-filter-assigned" value={fAssigned} onChange={(e) => setFAssigned(e.target.value)} className="md:col-span-2 py-2 px-3 text-sm bg-transparent border border-[#E8E6DF] rounded-md focus:outline-none">
                <option value="all">All assignees</option>
                <option value="__unassigned__">Unassigned</option>
                {assignedOptions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select data-testid="lead-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="md:col-span-2 py-2 px-3 text-sm bg-transparent border border-[#E8E6DF] rounded-md focus:outline-none">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="follow">Follow-up soonest</option>
                <option value="name">Name (A–Z)</option>
              </select>
            </div>

            {/* Leads table */}
            <div className="mt-6 overflow-x-auto border border-[#E8E6DF] bg-white rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F3EC] text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-4 w-8"></th>
                    <th className="text-left p-4">Created</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Contact</th>
                    <th className="text-left p-4">Source</th>
                    <th className="text-left p-4">Assigned</th>
                    <th className="text-left p-4">Follow-up</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((l) => {
                    const st = STATUS_MAP[l.status] || STATUS_MAP.new;
                    const waNum = toWa(l.phone);
                    const telNum = toTel(l.phone);
                    const overdue = isOverdue(l.follow_up_date);
                    const isOpen = expanded === l.id;
                    return (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={(e) => { if (e.target.closest('a,button')) return; toggleExpand(l.id); }} className={`border-t border-[#E8E6DF] cursor-pointer hover:bg-[#FBF8F2] ${overdue ? "bg-red-50/40" : ""} ${isOpen ? "bg-[#F5F3EC]" : ""}`}>
                          <td className="p-4">
                            <button data-testid={`lead-expand-${l.id}`} onClick={(e) => { e.stopPropagation(); toggleExpand(l.id); }} className="text-[#5C5C5C] hover:text-[#1C1A17]">
                              {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                            </button>
                          </td>
                          <td className="p-4 text-[#5C5C5C] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                          <td className="p-4 font-medium">{l.name}</td>
                          <td className="p-4 text-[#5C5C5C]">{l.phone}<br/><span className="text-xs">{l.email}</span></td>
                          <td className="p-4 text-xs">{l.source || "Unknown"}</td>
                          <td className="p-4 text-xs">{l.assigned_to || <span className="text-[#8A847D]">—</span>}</td>
                          <td className={`p-4 text-xs whitespace-nowrap ${overdue ? "text-red-600 font-medium" : ""}`}>
                            {l.follow_up_date ? new Date(l.follow_up_date).toLocaleDateString("en-IN", {day:"2-digit",month:"short"}) : <span className="text-[#8A847D]">—</span>}
                            {overdue && <span className="ml-1">(overdue)</span>}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${st.color}`}>{st.label}</span>
                              {l.estimates_count > 0 && (
                                <span data-testid={`lead-est-badge-${l.id}`} title="Estimator submissions"
                                      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F0E6] text-[#A07F2D] border border-[#E8D9B3]">
                                  Est: {l.estimates_count}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1.5">
                              {telNum && <a data-testid={`lead-call-${l.id}`} href={`tel:${telNum}`} title="Call" className="p-1.5 rounded-md border border-[#E8E6DF] hover:border-[#1C1A17]"><Phone size={14}/></a>}
                              {waNum && <a data-testid={`lead-wa-${l.id}`} href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1.5 rounded-md border border-[#E8E6DF] hover:border-[#25D366] text-[#25D366]"><MessageCircle size={14}/></a>}
                              {l.email && <a data-testid={`lead-email-${l.id}`} href={`mailto:${l.email}`} title="Email" className="p-1.5 rounded-md border border-[#E8E6DF] hover:border-[#1C1A17]"><Mail size={14}/></a>}
                            </div>
                          </td>
                        </tr>
                    );
                  })}
                  {filteredLeads.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-[#5C5C5C]">No leads match your filters.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* ---------- Right-side Lead Drawer ---------- */}
            {expanded && (() => {
              const l = leads.find((x) => x.id === expanded);
              if (!l) return null;
              const waNum = toWa(l.phone);
              const telNum = toTel(l.phone);
              const st = STATUS_MAP[l.status] || STATUS_MAP.new;
              return (
                <>
                  <div data-testid="lead-drawer-backdrop" onClick={() => { setExpanded(null); setDeleteConfirm(false); }} className="fixed inset-0 bg-black/30 z-40" />
                  <aside data-testid="lead-drawer" className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#FBF8F2] z-50 shadow-2xl overflow-y-auto animate-slideIn">
                    <div className="flex items-start justify-between p-6 border-b border-[#E8E6DF] bg-white">
                      <div className="pr-4">
                        <p className="overline text-[#A07F2D]">Lead</p>
                        <h2 className="font-heading text-2xl mt-1">{l.name || "—"}</h2>
                        <p className="text-xs text-[#5C5C5C] mt-1">{fmtDate(l.created_at)} · {l.source || "Unknown"}</p>
                      </div>
                      <button data-testid="lead-drawer-close" onClick={() => { setExpanded(null); setDeleteConfirm(false); }} className="p-2 rounded-full hover:bg-[#F5F3EC]"><X size={18}/></button>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Contact + quick actions */}
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${st.color}`}>{st.label}</span>
                        {l.estimates_count > 0 && <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F0E6] text-[#A07F2D] border border-[#E8D9B3]">Est: {l.estimates_count}</span>}
                        <div className="ml-auto flex items-center gap-1.5">
                          {telNum && <a data-testid="drawer-call" href={`tel:${telNum}`} className="p-2 rounded-md border border-[#E8E6DF] hover:border-[#1C1A17]"><Phone size={14}/></a>}
                          {waNum && <a data-testid="drawer-wa" href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-[#E8E6DF] hover:border-[#25D366] text-[#25D366]"><MessageCircle size={14}/></a>}
                          {l.email && <a data-testid="drawer-email" href={`mailto:${l.email}`} className="p-2 rounded-md border border-[#E8E6DF] hover:border-[#1C1A17]"><Mail size={14}/></a>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Phone</p><p className="mt-1">{l.phone || "—"}</p></div>
                        <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Email</p><p className="mt-1 break-all">{l.email || "—"}</p></div>
                        {l.apartment_type && <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Apartment</p><p className="mt-1">{l.apartment_type}</p></div>}
                        {l.apartment_size && <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Area</p><p className="mt-1">{l.apartment_size} sqft</p></div>}
                        {l.budget && <div className="col-span-2"><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Budget</p><p className="mt-1">{l.budget}</p></div>}
                        {Array.isArray(l.sources) && l.sources.length > 1 && (
                          <div className="col-span-2"><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">All sources</p><p className="mt-1 text-xs text-[#5C5C5C]">{l.sources.join(" · ")}</p></div>
                        )}
                        {l.message && <div className="col-span-2"><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Message</p><p className="mt-1 text-xs italic">"{l.message}"</p></div>}
                        {l.floor_plan_path && <div className="col-span-2"><a href={`/api/files/${l.floor_plan_path}`} target="_blank" rel="noreferrer" className="text-[#B38F24] link-underline text-xs">View floor plan ↗</a></div>}
                      </div>

                      {/* Editable fields — reuse existing draft/save mechanics */}
                      <div className="border-t border-[#E8E6DF] pt-5 grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Status</label>
                          <select data-testid={`lead-status-${l.id}`} value={val(l, "status") || "new"} onChange={(e) => setDraft(l.id, "status", e.target.value)} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md">
                            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Source</label>
                          <select data-testid={`lead-source-${l.id}`} value={val(l, "source") || ""} onChange={(e) => setDraft(l.id, "source", e.target.value)} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md">
                            <option value="">Unknown / Existing</option>
                            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Assigned to</label>
                          <input data-testid={`lead-assigned-${l.id}`} value={val(l, "assigned_to")} onChange={(e) => setDraft(l.id, "assigned_to", e.target.value)} placeholder="e.g. Priya" className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Follow-up date</label>
                          <input type="date" data-testid={`lead-followup-${l.id}`} value={val(l, "follow_up_date") ? String(val(l, "follow_up_date")).slice(0,10) : ""} onChange={(e) => setDraft(l.id, "follow_up_date", e.target.value || null)} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Internal notes</label>
                          <textarea data-testid={`lead-notes-${l.id}`} value={val(l, "notes")} onChange={(e) => setDraft(l.id, "notes", e.target.value)} rows={4} placeholder="Add internal notes about this lead…" className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <button data-testid={`lead-save-${l.id}`} onClick={() => saveDraft(l.id)} className="rounded-full bg-[#1C1A17] text-white px-6 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#2D2925]">Save changes</button>
                        </div>
                      </div>

                      {/* Estimate history */}
                      <div className="border-t border-[#E8E6DF] pt-5" data-testid={`lead-history-${l.id}`}>
                        <p className="overline text-[#A07F2D]">Estimate history</p>
                        {(historyByLead[l.id] || []).length === 0 && (
                          <p className="text-xs text-[#8A847D] mt-2">No estimator submissions linked to this lead yet.</p>
                        )}
                        {(historyByLead[l.id] || []).length > 0 && (
                          <div className="mt-3 border border-[#E8E6DF] rounded-md overflow-hidden bg-white">
                            <table className="w-full text-xs">
                              <thead className="bg-[#F5F3EC] text-[#5C5C5C]">
                                <tr>
                                  <th className="text-left p-2.5">Date</th>
                                  <th className="text-left p-2.5">BHK</th>
                                  <th className="text-left p-2.5">Finish</th>
                                  <th className="text-left p-2.5">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(historyByLead[l.id] || []).map((e) => (
                                  <tr key={e.id} className="border-t border-[#E8E6DF]">
                                    <td className="p-2.5 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                                    <td className="p-2.5">{e.bhk}</td>
                                    <td className="p-2.5 capitalize">{e.finish_level}</td>
                                    <td className="p-2.5 font-medium">{inr(e.total)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Danger zone — delete lead */}
                      <div className="border-t border-red-200 pt-5" data-testid={`lead-danger-${l.id}`}>
                        <p className="overline text-red-700">Danger zone</p>
                        {!deleteConfirm && (
                          <div className="mt-3 flex items-center justify-between gap-4">
                            <p className="text-xs text-[#5C5C5C]">Permanently delete this lead. This cannot be undone.</p>
                            <button
                              data-testid={`lead-delete-btn-${l.id}`}
                              onClick={() => setDeleteConfirm(true)}
                              className="rounded-full border border-red-300 text-red-700 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-red-50 whitespace-nowrap"
                            >
                              Delete lead
                            </button>
                          </div>
                        )}
                        {deleteConfirm && (
                          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-4" data-testid={`lead-delete-confirm-${l.id}`}>
                            <p className="text-sm text-red-900 font-medium">Delete this lead permanently?</p>
                            <p className="text-xs text-red-800/80 mt-1">
                              {(historyByLead[l.id] || []).length > 0
                                ? `This lead has ${(historyByLead[l.id] || []).length} linked estimate(s). Deletion will be blocked by the server until estimates are reassigned or archived.`
                                : "No estimates are linked to this lead. Deletion cannot be undone."}
                            </p>
                            <div className="mt-3 flex items-center gap-2 justify-end">
                              <button
                                data-testid={`lead-delete-cancel-${l.id}`}
                                onClick={() => setDeleteConfirm(false)}
                                disabled={deleteSaving}
                                className="rounded-full border border-[#E8E6DF] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-white disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                data-testid={`lead-delete-confirm-btn-${l.id}`}
                                onClick={() => deleteLead(l.id)}
                                disabled={deleteSaving}
                                className="rounded-full bg-red-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-red-800 disabled:opacity-50"
                              >
                                {deleteSaving ? "Deleting…" : "Yes, delete"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </aside>
                </>
              );
            })()}

            {/* ---------- Add Lead Modal ---------- */}
            {addLeadOpen && (
              <>
                <div onClick={() => !addLeadSaving && setAddLeadOpen(false)} className="fixed inset-0 bg-black/40 z-40" />
                <div data-testid="add-lead-modal" className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4">
                  <form onSubmit={submitNewLead} className="w-full max-w-lg bg-white rounded-lg shadow-2xl">
                    <div className="p-6 border-b border-[#E8E6DF] flex items-start justify-between">
                      <div>
                        <p className="overline text-[#A07F2D]">New lead</p>
                        <h3 className="font-heading text-2xl mt-1">Add a lead manually</h3>
                        <p className="text-xs text-[#5C5C5C] mt-1">For walk-ins, phone enquiries, referrals — anything not captured via the site.</p>
                      </div>
                      <button type="button" onClick={() => setAddLeadOpen(false)} className="p-2 rounded-full hover:bg-[#F5F3EC]"><X size={18}/></button>
                    </div>
                    <div className="p-6 grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Name *</label>
                        <input required data-testid="add-lead-name" value={newLead.name} onChange={(e) => setNewLead({...newLead, name: e.target.value})} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Phone *</label>
                        <input required data-testid="add-lead-phone" value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} placeholder="+91 98…" className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Email *</label>
                        <input required type="email" data-testid="add-lead-email" value={newLead.email} onChange={(e) => setNewLead({...newLead, email: e.target.value})} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Source</label>
                        <select data-testid="add-lead-source" value={newLead.source} onChange={(e) => setNewLead({...newLead, source: e.target.value})} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md">
                          {["Walk-in","Phone","Referral","Instagram","Website","Other"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Apartment</label>
                        <input data-testid="add-lead-apt" value={newLead.apartment_type} onChange={(e) => setNewLead({...newLead, apartment_type: e.target.value})} placeholder="e.g. 3BHK" className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Area (sqft)</label>
                        <input data-testid="add-lead-size" type="number" value={newLead.apartment_size} onChange={(e) => setNewLead({...newLead, apartment_size: e.target.value})} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Budget</label>
                        <input data-testid="add-lead-budget" value={newLead.budget} onChange={(e) => setNewLead({...newLead, budget: e.target.value})} placeholder="e.g. ₹15L - ₹25L" className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Notes</label>
                        <textarea data-testid="add-lead-message" value={newLead.message} onChange={(e) => setNewLead({...newLead, message: e.target.value})} rows={2} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                      </div>
                    </div>
                    <div className="p-6 border-t border-[#E8E6DF] flex items-center justify-end gap-2">
                      <button type="button" onClick={() => setAddLeadOpen(false)} className="rounded-full border border-[#E8E6DF] px-5 py-2 text-xs uppercase tracking-[0.2em]">Cancel</button>
                      <button type="submit" data-testid="add-lead-submit" disabled={addLeadSaving} className="rounded-full bg-[#C9A24A] text-white px-6 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#B38F24] disabled:opacity-50">
                        {addLeadSaving ? "Saving…" : "Save lead"}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "estimates" && (
          <div>
            <p className="overline">Estimates</p>
            <h1 className="font-heading text-4xl mt-2">Cost estimator submissions</h1>
            <div className="mt-8 overflow-x-auto border border-[#E8E6DF] bg-white rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F3EC] text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-4">When</th>
                    <th className="text-left p-4">Customer</th>
                    <th className="text-left p-4">Phone</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">BHK</th>
                    <th className="text-left p-4">Area</th>
                    <th className="text-left p-4">Finish</th>
                    <th className="text-left p-4">Total</th>
                    <th className="text-left p-4">Range</th>
                    <th className="text-left p-4">Lead</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((e) => {
                    const linkedLead = e.lead_id ? leads.find((l) => l.id === e.lead_id) : null;
                    return (
                      <tr key={e.id} className="border-t border-[#E8E6DF]">
                        <td className="p-4 text-[#5C5C5C] whitespace-nowrap">{fmtDate(e.created_at)}</td>
                        <td className="p-4 font-medium">{e.name || "—"}</td>
                        <td className="p-4">{e.phone || "—"}</td>
                        <td className="p-4 text-xs">{e.email || "—"}</td>
                        <td className="p-4">{e.bhk}</td>
                        <td className="p-4">{e.size_sqft} sqft</td>
                        <td className="p-4 capitalize">{e.finish_level}</td>
                        <td className="p-4 font-medium">{inr(e.total)}</td>
                        <td className="p-4 text-xs text-[#5C5C5C]">{e.range_low ? `${inr(e.range_low)} – ${inr(e.range_high)}` : "—"}</td>
                        <td className="p-4 text-xs">
                          {linkedLead ? (
                            <button data-testid={`est-lead-link-${e.id}`} onClick={() => { setTab("leads"); toggleExpand(linkedLead.id); }} className="text-[#B38F24] link-underline">
                              {linkedLead.name || linkedLead.phone || "View lead"}
                            </button>
                          ) : (
                            <span className="text-[#8A847D]">Unlinked</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {estimates.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-[#5C5C5C]">No estimates yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div>
            <p className="overline">Projects</p>
            <h1 className="font-heading text-4xl mt-2">Active & past projects</h1>

            <form onSubmit={createProject} className="mt-8 grid grid-cols-1 md:grid-cols-6 gap-4 border border-[#E8E6DF] p-6 bg-white">
              <input data-testid="proj-title" required placeholder="Title" className="input-line md:col-span-2" value={newProj.title} onChange={(e) => setNewProj({...newProj, title: e.target.value})} />
              <input required placeholder="Location" className="input-line md:col-span-2" value={newProj.location} onChange={(e) => setNewProj({...newProj, location: e.target.value})} />
              <select className="input-line" value={newProj.bhk} onChange={(e) => setNewProj({...newProj, bhk: e.target.value})}>
                {["1BHK","2BHK","3BHK","4BHK","Villa"].map((x) => <option key={x}>{x}</option>)}
              </select>
              <select className="input-line" value={newProj.finish_level} onChange={(e) => setNewProj({...newProj, finish_level: e.target.value})}>
                {["essential","premium","luxury"].map((x) => <option key={x}>{x}</option>)}
              </select>
              <select className="input-line md:col-span-2" value={newProj.status} onChange={(e) => setNewProj({...newProj, status: e.target.value})}>
                {["lead","design","execution","handover","done"].map((x) => <option key={x}>{x}</option>)}
              </select>
              <input className="input-line md:col-span-3" placeholder="Quotation Amount (₹)" type="number" value={newProj.quotation_amount} onChange={(e) => setNewProj({...newProj, quotation_amount: e.target.value})} />
              <button data-testid="proj-create" className="btn-primary md:col-span-1">Create</button>
            </form>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => {
                const editing = editingProj === p.id;
                return (
                  <div key={p.id} data-testid={`proj-card-${p.id}`} className="border border-[#E8E6DF] p-6 bg-white">
                    {!editing ? (
                      <>
                        <p className="overline">{p.status}</p>
                        <p className="font-heading text-2xl mt-2">{p.title}</p>
                        <p className="text-sm text-[#5C5C5C] mt-1">{p.location} · {p.bhk} · {p.finish_level}</p>
                        <p className="text-sm mt-4">{inr(p.quotation_amount)}</p>
                        <div className="mt-5 flex items-center gap-2">
                          <button data-testid={`proj-edit-${p.id}`} onClick={() => startEditProject(p)} className="rounded-full border border-[#E8E6DF] px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:border-[#1C1A17]">Edit</button>
                          <button data-testid={`proj-delete-${p.id}`} onClick={() => deleteProject(p)} className="rounded-full border border-red-200 text-red-700 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-red-50">Delete</button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <input data-testid={`proj-edit-title-${p.id}`} className="input-line w-full" value={projDraft.title} onChange={(e) => setProjDraft({...projDraft, title: e.target.value})} placeholder="Title" />
                        <input className="input-line w-full" value={projDraft.location} onChange={(e) => setProjDraft({...projDraft, location: e.target.value})} placeholder="Location" />
                        <div className="grid grid-cols-2 gap-2">
                          <select className="input-line" value={projDraft.bhk} onChange={(e) => setProjDraft({...projDraft, bhk: e.target.value})}>
                            {["1BHK","2BHK","3BHK","4BHK","Villa"].map((x) => <option key={x}>{x}</option>)}
                          </select>
                          <select className="input-line" value={projDraft.finish_level} onChange={(e) => setProjDraft({...projDraft, finish_level: e.target.value})}>
                            {["essential","premium","luxury"].map((x) => <option key={x}>{x}</option>)}
                          </select>
                        </div>
                        <select className="input-line w-full" value={projDraft.status} onChange={(e) => setProjDraft({...projDraft, status: e.target.value})}>
                          {["lead","design","execution","handover","done"].map((x) => <option key={x}>{x}</option>)}
                        </select>
                        <input className="input-line w-full" placeholder="Quotation Amount (₹)" type="number" value={projDraft.quotation_amount} onChange={(e) => setProjDraft({...projDraft, quotation_amount: e.target.value})} />
                        <div className="flex gap-2 pt-2">
                          <button data-testid={`proj-save-${p.id}`} onClick={() => saveEditProject(p.id)} className="rounded-full bg-[#1C1A17] text-white px-5 py-1.5 text-[10px] uppercase tracking-[0.2em]">Save</button>
                          <button onClick={() => { setEditingProj(null); setProjDraft({}); }} className="rounded-full border border-[#E8E6DF] px-5 py-1.5 text-[10px] uppercase tracking-[0.2em]">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {projects.length === 0 && <p className="text-sm text-[#5C5C5C]">No projects yet. Create one above.</p>}
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div>
            <p className="overline">Portfolio</p>
            <h1 className="font-heading text-4xl mt-2">Public showcase</h1>

            {/* ---------- Add new portfolio item ---------- */}
            <form onSubmit={addPortfolio} className="mt-8 border border-[#E8E6DF] p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <input required data-testid="port-title" placeholder="Title" className="input-line md:col-span-3" value={newPort.title} onChange={(e) => setNewPort({...newPort, title: e.target.value})} />
                <input required data-testid="port-location" placeholder="Location" className="input-line md:col-span-3" value={newPort.location} onChange={(e) => setNewPort({...newPort, location: e.target.value})} />
                <select className="input-line md:col-span-2" value={newPort.bhk} onChange={(e) => setNewPort({...newPort, bhk: e.target.value})}>
                  {["1BHK","2BHK","3BHK","4BHK","Villa"].map((x) => <option key={x}>{x}</option>)}
                </select>
                <input className="input-line md:col-span-2" placeholder="Style" value={newPort.style} onChange={(e) => setNewPort({...newPort, style: e.target.value})} />
                <input className="input-line md:col-span-2" placeholder="Description" value={newPort.description} onChange={(e) => setNewPort({...newPort, description: e.target.value})} />
              </div>

              {/* Image uploader */}
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <p className="overline">Project photos <span className="text-[#8A847D] normal-case tracking-normal ml-2">({(newPort.images || []).length} / 10)</span></p>
                  <label className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.2em] cursor-pointer ${(newPort.images || []).length >= 10 ? "opacity-50 cursor-not-allowed border-[#E8E6DF]" : "border-[#1C1A17] hover:bg-[#1C1A17] hover:text-white"}`}>
                    <ImagePlus size={14} /> {portUploading ? "Uploading…" : "Upload photos"}
                    <input data-testid="port-upload" type="file" hidden multiple accept="image/jpeg,image/jpg,image/png,image/webp"
                      disabled={(newPort.images || []).length >= 10 || portUploading}
                      onChange={(e) => handlePortUpload(e.target.files, newPort, setNewPort, setPortUploading)} />
                  </label>
                </div>

                {(newPort.images || []).length === 0 && (
                  <p className="text-xs text-[#8A847D] mt-3">At least one photo is required. JPG, PNG or WEBP, max 10MB each.</p>
                )}

                {(newPort.images || []).length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {(newPort.images || []).map((im) => (
                      <div key={im.id} className={`relative rounded-md overflow-hidden border-2 ${newPort.cover_image_id === im.id ? "border-[#C9A24A]" : "border-transparent"}`}>
                        <img src={im.url} alt="" className="w-full aspect-square object-cover" />
                        {newPort.cover_image_id === im.id && (
                          <span className="absolute top-2 left-2 bg-[#C9A24A] text-white text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full">Cover</span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex gap-1 opacity-0 hover:opacity-100 transition-opacity">
                          {newPort.cover_image_id !== im.id && (
                            <button type="button" onClick={() => setPortCover(im.id, newPort, setNewPort)} className="text-[10px] px-2 py-1 rounded bg-white/90 text-[#1C1A17]">Set cover</button>
                          )}
                          <button type="button" onClick={() => removePortImage(im.id, newPort, setNewPort)} className="text-[10px] px-2 py-1 rounded bg-red-600 text-white ml-auto">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button data-testid="port-add" className="btn-primary flex items-center gap-2"><ImagePlus size={14}/> Publish</button>
              </div>
            </form>

            {/* ---------- Existing portfolio list ---------- */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolio.map((p) => {
                const editing = editingPort === p.id;
                const draft = portEditDraft;
                return (
                  <div key={p.id} data-testid={`port-card-${p.id}`} className="border border-[#E8E6DF] bg-white">
                    {!editing ? (
                      <>
                        <img src={p.cover_image} alt={p.title} className="w-full aspect-[4/3] object-cover" />
                        <div className="p-4">
                          <p className="font-heading text-xl">{p.title}</p>
                          <p className="text-xs text-[#5C5C5C] mt-1">{p.location} · {p.bhk} · {p.style}</p>
                          <p className="text-[10px] text-[#8A847D] mt-2">{(p.images || []).length} photo{(p.images || []).length === 1 ? "" : "s"}</p>
                          <div className="mt-4 flex gap-2">
                            <button data-testid={`port-edit-${p.id}`} onClick={() => startEditPortfolio(p)} className="rounded-full border border-[#E8E6DF] px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:border-[#1C1A17]">Edit</button>
                            <button data-testid={`port-delete-${p.id}`} onClick={() => deletePortfolio(p.id)} className="rounded-full border border-red-200 text-red-700 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-red-50">Delete</button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="p-4 space-y-3">
                        <input className="input-line w-full" value={draft.title} onChange={(e) => setPortEditDraft({...draft, title: e.target.value})} placeholder="Title" />
                        <input className="input-line w-full" value={draft.location} onChange={(e) => setPortEditDraft({...draft, location: e.target.value})} placeholder="Location" />
                        <div className="grid grid-cols-2 gap-2">
                          <select className="input-line" value={draft.bhk} onChange={(e) => setPortEditDraft({...draft, bhk: e.target.value})}>
                            {["1BHK","2BHK","3BHK","4BHK","Villa"].map((x) => <option key={x}>{x}</option>)}
                          </select>
                          <input className="input-line" value={draft.style} onChange={(e) => setPortEditDraft({...draft, style: e.target.value})} placeholder="Style" />
                        </div>
                        <input className="input-line w-full" value={draft.description} onChange={(e) => setPortEditDraft({...draft, description: e.target.value})} placeholder="Description" />

                        <div>
                          <div className="flex items-center justify-between">
                            <p className="overline">Photos <span className="text-[#8A847D] normal-case tracking-normal">({(draft.images || []).length}/10)</span></p>
                            <label className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] cursor-pointer ${(draft.images || []).length >= 10 ? "opacity-50 cursor-not-allowed border-[#E8E6DF]" : "border-[#1C1A17]"}`}>
                              <ImagePlus size={12} /> {portEditUploading ? "…" : "Add"}
                              <input type="file" hidden multiple accept="image/jpeg,image/jpg,image/png,image/webp"
                                disabled={(draft.images || []).length >= 10 || portEditUploading}
                                onChange={(e) => handlePortUpload(e.target.files, draft, setPortEditDraft, setPortEditUploading)} />
                            </label>
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {(draft.images || []).map((im) => (
                              <div key={im.id} className={`relative rounded overflow-hidden border-2 ${draft.cover_image_id === im.id ? "border-[#C9A24A]" : "border-transparent"}`}>
                                <img src={im.url} alt="" className="w-full aspect-square object-cover" />
                                {draft.cover_image_id === im.id && <span className="absolute top-1 left-1 bg-[#C9A24A] text-white text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full">Cover</span>}
                                <div className="absolute inset-x-0 bottom-0 bg-black/60 flex text-[9px]">
                                  {draft.cover_image_id !== im.id && <button type="button" onClick={() => setPortCover(im.id, draft, setPortEditDraft)} className="flex-1 text-white py-0.5">Cover</button>}
                                  <button type="button" onClick={() => removePortImage(im.id, draft, setPortEditDraft)} className="flex-1 text-red-300 py-0.5">Remove</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button data-testid={`port-save-${p.id}`} onClick={() => saveEditPortfolio(p.id)} className="rounded-full bg-[#1C1A17] text-white px-5 py-1.5 text-[10px] uppercase tracking-[0.2em]">Save</button>
                          <button onClick={() => { setEditingPort(null); setPortEditDraft({}); }} className="rounded-full border border-[#E8E6DF] px-5 py-1.5 text-[10px] uppercase tracking-[0.2em]">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* ---------- Change Password Modal ---------- */}
      {cpOpen && (
        <>
          <div onClick={() => !cpSaving && setCpOpen(false)} className="fixed inset-0 bg-black/40 z-40" />
          <div data-testid="change-password-modal" className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4">
            <form onSubmit={submitChangePassword} className="w-full max-w-md bg-white rounded-lg shadow-2xl">
              <div className="p-6 border-b border-[#E8E6DF] flex items-start justify-between">
                <div>
                  <p className="overline text-[#A07F2D]">Security</p>
                  <h3 className="font-heading text-2xl mt-1">Change password</h3>
                  <p className="text-xs text-[#5C5C5C] mt-1">You'll be signed out after changing. Sign in again with the new password.</p>
                </div>
                <button type="button" onClick={() => setCpOpen(false)} className="p-2 rounded-full hover:bg-[#F5F3EC]"><X size={18}/></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Current password</label>
                  <input required autoFocus data-testid="cp-current" type="password" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">New password <span className="text-[#8A847D] normal-case tracking-normal">(min 10 characters)</span></label>
                  <input required data-testid="cp-next" type="password" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Confirm new password</label>
                  <input required data-testid="cp-confirm" type="password" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} className="w-full mt-1 py-2 px-3 text-sm bg-white border border-[#E8E6DF] rounded-md" />
                </div>
              </div>
              <div className="p-6 border-t border-[#E8E6DF] flex items-center justify-end gap-2">
                <button type="button" onClick={() => setCpOpen(false)} className="rounded-full border border-[#E8E6DF] px-5 py-2 text-xs uppercase tracking-[0.2em]">Cancel</button>
                <button type="submit" data-testid="cp-submit" disabled={cpSaving} className="rounded-full bg-[#1C1A17] text-white px-6 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#2D2925] disabled:opacity-50">
                  {cpSaving ? "Updating…" : "Update password"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
